import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { LandingFooterData } from '../types';

interface LandingFooterProps {
    data: LandingFooterData;
}

/**
 * Footer section with company info, links, and social.
 */
const LandingFooter: React.FC<LandingFooterProps> = ({ data }) => {
    const { t } = useTranslation();

    return (
        <footer className="bg-slate-950 border-t border-slate-800">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
                    {/* Company Info */}
                    <div className="lg:col-span-2">
                        <div className="text-2xl font-bold text-white mb-4">
                            {data.companyName}
                        </div>
                        <p className="text-slate-400 leading-relaxed mb-6 max-w-md">
                            {data.description}
                        </p>
                        {/* Social Links */}
                        <div className="flex items-center gap-4">
                            {data.social.map((link, index) => (
                                <a
                                    key={index}
                                    href={link.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-10 h-10 rounded-lg bg-slate-800 hover:bg-violet-600 flex items-center justify-center transition-colors group"
                                >
                                    {link.platform === 'zalo' && (
                                        <svg className="w-5 h-5 text-slate-400 group-hover:text-white" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm5 14.5c0 .83-.67 1.5-1.5 1.5h-7c-.83 0-1.5-.67-1.5-1.5v-9c0-.83.67-1.5 1.5-1.5h7c.83 0 1.5.67 1.5 1.5v9z" />
                                        </svg>
                                    )}
                                    {link.platform === 'youtube' && (
                                        <svg className="w-5 h-5 text-slate-400 group-hover:text-white" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                                        </svg>
                                    )}
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h4 className="text-white font-semibold mb-4">
                            {t('landing.footer.quickLinks', 'Quick Links')}
                        </h4>
                        <ul className="space-y-3">
                            {data.quickLinks.map((link, index) => (
                                <li key={index}>
                                    <Link
                                        to={link.href}
                                        className="text-slate-400 hover:text-violet-400 transition-colors"
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Legal & Contact */}
                    <div>
                        <h4 className="text-white font-semibold mb-4">
                            {t('landing.footer.legal', 'Legal')}
                        </h4>
                        <ul className="space-y-3 mb-6">
                            {data.legal.map((link, index) => (
                                <li key={index}>
                                    <Link
                                        to={link.href}
                                        className="text-slate-400 hover:text-violet-400 transition-colors"
                                    >
                                        {link.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>

                    </div>
                </div>

                {/* Copyright */}
                <div className="border-t border-slate-800 mt-12 pt-8 text-center">
                    <p className="text-slate-500 text-sm">
                        {data.copyright}
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default LandingFooter;
